import { action, observable, makeObservable } from 'mobx';
import {debounce, Disposers} from '../../../../../../lib/helpers';
import {TimeRange} from '../models';
import {isTimeRangesEqual} from '../utils';

function getDuration(audio: HTMLAudioElement | null): number {
  if (audio === null)
    return 0;
  // if audio is not loaded, NaN
  // if audio is streaming, infinity
  if (isFinite(audio.duration))
    return audio.duration;
  else
    return 0;
}

export class AudioElementStore {
  @observable.ref
  element: HTMLAudioElement | null = null;

  @observable.ref
  buffered: readonly TimeRange[] = [];

  @observable
  duration = 0;

  @observable
  currentTime = 0;

  @observable
  paused = true;

  @observable
  ended = false;

  @observable
  playCount = 0;

  @observable
  muted = true;

  @observable
  playFrom = 0;

  @observable
  isAudioError = false;

  private readonly disposers = new Disposers();

  @action
  setElement = (element: HTMLAudioElement) => {
    this.element = element;
    this.buffered = [];

    this.addEventListeners(element);
    this.disposers.add(this.removeEventListeners(element));
  };

  private addEventListeners = (element: HTMLAudioElement) => {
    element.addEventListener('loadedmetadata', this.setDuration);
    element.addEventListener('durationchange', this.setDuration);
    element.addEventListener('progress', this.handleAudioDownloadProgressUpdate);
    element.addEventListener('timeupdate', this.debouncedOnTimeUpdate);
    element.addEventListener('pause', this.updatePausedOrEnded);
    element.addEventListener('play', this.updatePausedOrEnded);
    element.addEventListener('playing', this.updatePausedOrEnded);
    element.addEventListener('waiting', this.updatePausedOrEnded);
    element.addEventListener('ended', this.updatePausedOrEnded);
  };

  private removeEventListeners = (element: HTMLAudioElement) => () => {
    element.removeEventListener('loadedmetadata', this.setDuration);
    element.removeEventListener('durationchange', this.setDuration);
    element.removeEventListener('progress', this.handleAudioDownloadProgressUpdate);
    element.removeEventListener('timeupdate', this.debouncedOnTimeUpdate);
    element.removeEventListener('pause', this.updatePausedOrEnded);
    element.removeEventListener('play', this.updatePausedOrEnded);
    element.removeEventListener('playing', this.updatePausedOrEnded);
    element.removeEventListener('waiting', this.updatePausedOrEnded);
    element.removeEventListener('ended', this.updatePausedOrEnded);
  };

  @action
  setCurrentTime = (currentTime: number) => {
    if (this.element === null)
      return;
    this.element.currentTime = currentTime;
    if (!this.paused) {
      this.playFrom = currentTime;
      this.playCount++;
    }
  };

  @action
  togglePlay = () => {
    if (this.element === null)
      return;
    if (this.paused || this.ended)
      this.play();
    else
      this.pause();
  };

  @action
  play = () => {
    if (this.element === null)
      return;
    if (this.ended)
      this.setCurrentTime(0);
    this.playFrom = this.currentTime;
    this.playCount++;
    this.element.play();
  };

  @action
  pause = () => {
    if (this.element === null)
      return;
    this.element.pause();
  };

  @action
  toggleVolume = () => {
    if (this.element === null)
      return;
    this.element.muted = !this.element.muted;
    this.muted = this.element.muted;
  };

  @action
  private setDuration = () => {
    if (this.duration !== 0)
      return;
    this.duration = getDuration(this.element);
  };

  @action
  setAudioError = (value: boolean) => {
    this.isAudioError = value;
  }

  @action
  private handleAudioDownloadProgressUpdate = (): void => {
    const audio = this.element;
    if (audio === null) {
      return;
    }
    const progress: TimeRange[] = [];
    for (let i = 0; i < audio.buffered.length; i++) {
      const start: number = audio.buffered.start(i);
      const end: number = audio.buffered.end(i);
      progress.push({start: start, end: end});
    }
    if (isTimeRangesEqual(this.buffered, progress))
      return;
    this.buffered = progress;
  }

  @action
  private changeCurrentTime = (time: number) => {
    this.currentTime = time
  }

  @action
  private onTimeUpdate = () => {
    const audio = this.element;
    if (audio === null) {
      this.changeCurrentTime(0);
    }
    else {
      this.changeCurrentTime(audio.currentTime);
      // this.currentTime = audio.currentTime;
    }
  };

  private debouncedOnTimeUpdate = debounce(this.onTimeUpdate, 200);

  @action
  private updatePausedOrEnded = () => {
    this.paused = this.element!.paused;
    this.ended = this.element!.ended;
  };

  constructor() {
    makeObservable(this);
  }

  dispose() {
    this.disposers.dispose();
  }
}
